import { ArrowUp, ArrowDown, ArrowUpDown, X, Download } from "lucide-react";
import { FaSpinner } from "react-icons/fa";
import { useTableLogic } from "./useTableLogic";
import { FilterDropdown } from "./FilterDropdown";
import { useState } from "react";
import Pagination from "../pagination/Pagination";
import { useExportToExcel } from "./useExportToExcel";
import { formatarDataSemFuso } from "../../utils/formatDate";

const T = {
    bg: "#F7F9FC",
    surface: "#FFFFFF",
    surfaceAlt: "#F1F5F9",
    border: "#E2E8F0",
    borderStrong: "#CBD5E1",
    text: "#0F172A",
    textMid: "#475569",
    muted: "#94A3B8",
    primary: "#2563EB",
    primaryLight: "#EFF6FF",
    secondary: "#64748B",
    green: "#16A34A",
    greenLight: "#F0FDF4",
    red: "#DC2626",
    redLight: "#FEF2F2",
    blue: "#0EA5E9",
    blueLight: "#F0F9FF",
    acum: "#EEF2FF",
    acumText: "#4338CA",
    total: "#F8FAFC",
};

export function CustomTable({
    data,
    columns,
    className = "",
    footer,
    onRowSelect,
    header,
    showHeader = true,
    pagination,
    loading,
    pageSize = 20,
    maxHeight,
    exportable = false,
    exportFilename = "tabela",
    dense = false,
    hideLoadingSkeleton = false,
    // Seleção por checkbox (opt-in): `rowKey` = campo identificador da linha, `selectedKeys` = chaves
    // selecionadas (controlado pelo pai), `isRowSelectable` = quais linhas podem ser marcadas. O
    // checkbox do cabeçalho marca/desmarca todas as linhas selecionáveis do resultado filtrado (todas as páginas).
    selectable = false,
    rowKey = "id",
    selectedKeys = [],
    onSelectionChange,
    isRowSelectable = () => true,
}) {

    const { exportToExcel } = useExportToExcel();
    const [selectedRowIndex, setSelectedRowIndex] = useState(null);
    const [pageData, setPageData] = useState([]);

    const {
        processedData,
        sort,
        filters,
        toggleSort,
        toggleFilter,
        selectAllFilter,
        clearFilter,
        clearAllFilters,
        getFilterOptions,
        activeFilterCount,
        totalRows,
        filteredRows,
    } = useTableLogic(data, columns);

    const tableData = pagination ? pageData : processedData;

    const selecionadas = new Set(selectedKeys);
    const chavesSelecionaveis = selectable ? processedData.filter(isRowSelectable).map(r => r[rowKey]) : [];
    const todasMarcadas = chavesSelecionaveis.length > 0 && chavesSelecionaveis.every(k => selecionadas.has(k));
    const algumaMarcada = chavesSelecionaveis.some(k => selecionadas.has(k));

    function alternarLinha(row) {
        const chave = row[rowKey];
        const proximas = new Set(selecionadas);
        if (proximas.has(chave)) proximas.delete(chave);
        else proximas.add(chave);
        onSelectionChange?.(Array.from(proximas));
    }

    function alternarTodas() {
        const proximas = new Set(selecionadas);
        if (todasMarcadas) chavesSelecionaveis.forEach(k => proximas.delete(k));
        else chavesSelecionaveis.forEach(k => proximas.add(k));
        onSelectionChange?.(Array.from(proximas));
    }

    const totalColunas = columns.length + (selectable ? 1 : 0);

    const formatCell = (value, col, row, index) => {
        if (col.render) return col.render(value, row, index);
        if (value == null)
            return <span className="text-muted-foreground italic">—</span>;

        switch (col.format) {
            case "percent":
                return typeof value === "number"
                    ? `${value.toFixed(2)}%`
                    : String(value);

            case "currency":
                return typeof value === "number"
                    ? value.toLocaleString("pt-BR", {
                        style: "currency",
                        currency: "BRL",
                    })
                    : String(value);

            case "number":
                return typeof value === "number"
                    ? value.toLocaleString("pt-BR")
                    : String(value);

            case "date":
                return typeof value === "string"
                    ? formatarDataSemFuso(value)
                    : String(value);

            case "boolean":
                return value ? "Sim" : "Não";

            default:
                return String(value);
        }
    };

    const SortIcon = ({ colKey }) => {
        if (!sort || sort.key !== colKey)
            return <ArrowUpDown size={13} className="text-table-header-fg/40" />;

        if (sort.direction === "asc")
            return <ArrowUp size={13} className="text-blue-500" />;

        return <ArrowDown size={13} className="text-blue-500" />;
    };

    const handleRowClick = (row, index, event) => {
        setSelectedRowIndex(index);

        if (onRowSelect) {
            onRowSelect(row, event);
        }
    };

    return (
        <div className={`w-full ${className}`}>
            {/* Info bar */}
            <div className="flex items-center justify-between px-1">
                {showHeader ?
                    <span className="text-sm text-muted-foreground">
                        {filteredRows === totalRows
                            ? `${totalRows} registros`
                            : `${filteredRows} de ${totalRows} registros`}
                    </span>
                    : <div></div>
                }

                <div className="flex gap-2">
                    {activeFilterCount > 0 && (
                        <button
                            onClick={clearAllFilters}
                            className="text-xs px-2 py-1 bg-blue-100 my-1 rounded-full flex items-center gap-1 text-blue-600 hover:bg-blue-200"
                        >
                            <X size={14} /> Limpar filtros ({activeFilterCount})
                        </button>
                    )}

                    {header}

                </div>
            </div>

            {/* Table container com scroll */}
            <div
                className={`overflow-x-auto overflow-y-auto rounded-t-xl  ${loading && 'cursor-wait'}`}
                style={maxHeight ? { maxHeight} : undefined}
            >
                <table className={`w-full ${loading && 'cursor-not-allowed opacity-70 pointer-events-none cursor-wait'}`}>
                    <thead className="sticky top-0 z-10">
                        <tr className="bg-table-header text-table-header-fg">
                            {selectable && (
                                <th className={`${dense ? 'px-2 py-1.5' : 'px-3 py-2.5'} w-8`}>
                                    <input
                                        type="checkbox"
                                        aria-label="Selecionar todas"
                                        checked={todasMarcadas}
                                        ref={el => { if (el) el.indeterminate = algumaMarcada && !todasMarcadas; }}
                                        disabled={chavesSelecionaveis.length === 0}
                                        onChange={alternarTodas}
                                        className="h-4 w-4 cursor-pointer accent-primary"
                                    />
                                </th>
                            )}
                            {columns.map((col) => (
                                <th
                                    key={col.key}
                                    className={`${dense ? 'px-2 py-1.5' : 'px-3 py-2.5'} font-semibold whitespace-nowrap select-none ${col.center ? 'text-center' : 'text-left'} ${col.stickyLeft ? 'sticky left-0 z-20 bg-table-header' : ''}`}
                                    style={col.width ? { width: col.width } : undefined}
                                >
                                    <div className="flex items-center gap-1.5">
                                        <span>{col.label}</span>

                                        <div className="flex items-center gap-0.5">
                                            {col.filterable !== false && (
                                                <FilterDropdown
                                                    columnKey={col.key}
                                                    options={getFilterOptions(col.key)}
                                                    selectedValues={filters[col.key] || new Set()}
                                                    onToggle={toggleFilter}
                                                    onSelectAll={selectAllFilter}
                                                    onClear={clearFilter}
                                                    isActive={(filters[col.key]?.size || 0) > 0}
                                                />
                                            )}

                                            {col.sortable !== false && (
                                                <button
                                                    onClick={() => toggleSort(col.key)}
                                                    className="p-1 text-table-header-fg/50 rounded-md hover:bg-black/5 hover:text-table-header-fg transition-colors"
                                                >
                                                    <SortIcon colKey={col.key} />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </th>
                            ))}
                        </tr>
                    </thead>

                    <tbody>
                        {loading && !hideLoadingSkeleton ? (
                            <tr>
                                <td
                                    colSpan={totalColunas}
                                    className="px-3 py-10"
                                >
                                    <div className="flex items-center justify-center gap-2 text-muted-foreground">
                                        <FaSpinner className="animate-spin" size={18} />
                                        <span className="text-sm">Carregando...</span>
                                    </div>
                                </td>
                            </tr>
                        ) : tableData.length === 0 ? (
                            <tr>
                                <td
                                    colSpan={totalColunas}
                                    className="text-left px-3 py-8 text-muted-foreground"
                                >
                                    Nenhum registro encontrado
                                </td>
                            </tr>
                        ) : (
                            tableData.map((row, idx) => (
                                <tr
                                    key={idx}
                                    className={`border-b border-table-border hover:bg-table-row-hover transition-colors cursor-default ${selectable && selecionadas.has(row[rowKey]) ? 'bg-primary/5' : ''}`}
                                >
                                    {selectable && (
                                        <td className={`${dense ? 'px-2 py-1' : 'px-3 py-2'} w-8`} onClick={e => e.stopPropagation()}>
                                            <input
                                                type="checkbox"
                                                aria-label="Selecionar linha"
                                                checked={selecionadas.has(row[rowKey])}
                                                disabled={!isRowSelectable(row)}
                                                onChange={() => alternarLinha(row)}
                                                className="h-4 w-4 cursor-pointer accent-primary disabled:cursor-not-allowed disabled:opacity-40"
                                            />
                                        </td>
                                    )}
                                    {columns.map((col) => (
                                        <td
                                            key={col.key}
                                            onClick={e => {
                                                if (!col.noSelected) handleRowClick(row, idx, e)
                                            }}
                                            className={`${dense ? 'px-2 py-1' : 'px-3 py-2'} whitespace-nowrap text-foreground ${col.className ?? null}  ${col.center ? 'text-center' : 'text-left'} ${col.stickyLeft ? 'sticky left-0 z-[1] bg-card' : ''}`}
                                        >
                                            {formatCell(row[col.key], col, row, idx)}
                                        </td>
                                    ))}
                                </tr>
                            ))
                        )}
                    </tbody>


                </table>


            </div>

            {pagination && (
                <div className="pb-2 py-4 px-3">
                    <Pagination
                        data={processedData}
                        loading={loading}
                        initialPageSize={pageSize}
                        currentData={setPageData}
                    />
                </div>
            )}

            {footer && (
                <div className="bg-muted border border-t-0 border-border rounded-b-xl">
                    <div className="flex justify-between flex-row-reverse px-3">
                        {footer(processedData)}
                        {exportable && processedData.length > 0 && (
                            <button
                                onClick={() => exportToExcel(processedData, columns, exportFilename)}
                                className="text-xs flex items-center gap-1 text-muted-foreground hover:text-foreground hover:underline transition-colors"
                                title="Exportar para Excel"
                            >
                                <Download size={14} /> Exportar Excel
                            </button>
                        )}
                    </div>
                </div>
            )}

        </div>
    );
}